import type { SiteBrand } from '~/config/branding';
import { faqNavGroupsByBrand, faqSectionOrderForBrand } from './faqGroups';
import type { FaqNavGroup, FaqSection } from './faqTypes';

// Each section is authored as a standalone JSON file under
// ./faq-sections/<brand>/*.json. Vite eagerly bundles them here so adding a
// section is just dropping in a file (and referencing its id in ./faqGroups.ts).
//
// The globs must be two separate literals — Vite resolves `import.meta.glob`
// at build time by static analysis, so a template built from a `brand`
// variable matches nothing.
const modulesByBrand: Record<SiteBrand, Record<string, { default: FaqSection }>> = {
  mcv: import.meta.glob('./faq-sections/mcv/*.json', { eager: true }) as Record<
    string,
    { default: FaqSection }
  >,
  hlx: import.meta.glob('./faq-sections/hlx/*.json', { eager: true }) as Record<
    string,
    { default: FaqSection }
  >,
};

export type FaqBrandContent = {
  sections: FaqSection[];
  sectionById: Record<string, FaqSection>;
  sectionOrder: string[];
  navGroups: FaqNavGroup[];
};

function buildContent(brand: SiteBrand): FaqBrandContent {
  const modules = modulesByBrand[brand];
  const sectionById: Record<string, FaqSection> = {};

  for (const path in modules) {
    const section = modules[path]?.default;
    if (section && section.id) {
      if (import.meta.env.DEV && sectionById[section.id]) {
        console.warn(
          `Duplicate FAQ section id "${section.id}" — ${path} overwrites an earlier file.`
        );
      }
      sectionById[section.id] = section;
    }
  }

  const sectionOrder = faqSectionOrderForBrand(brand);

  // Sections in nav order. Any section file present but not listed in
  // faqGroups is appended at the end so content is never silently dropped.
  const orderedFromGroups = sectionOrder
    .map((id) => sectionById[id])
    .filter((section): section is FaqSection => Boolean(section));
  const orphaned = Object.values(sectionById).filter(
    (section) => !sectionOrder.includes(section.id)
  );

  if (import.meta.env.DEV && orphaned.length > 0) {
    console.warn(
      `FAQ sections not listed in faqGroups for "${brand}", appended at the end: ${orphaned
        .map((section) => section.id)
        .join(', ')}`
    );
  }

  return {
    sections: [...orderedFromGroups, ...orphaned],
    sectionById,
    sectionOrder,
    navGroups: faqNavGroupsByBrand[brand],
  };
}

const contentByBrand: Record<SiteBrand, FaqBrandContent> = {
  mcv: buildContent('mcv'),
  hlx: buildContent('hlx'),
};

export const faqContentForBrand = (brand: SiteBrand): FaqBrandContent => contentByBrand[brand];
